/**
 * News list — AJAX partial reload + pushState URLs (shareable filter links).
 */

function parseNewsListStateFromUrl(href, qk) {
	const url = href instanceof URL ? href : new URL(href, window.location.origin);
	const cat = url.searchParams.get(`${qk}_cat`) || '';
	const projRaw = url.searchParams.get(`${qk}_proj`);
	const proj = projRaw
		? projRaw
				.split(',')
				.map((x) => parseInt(x, 10))
				.filter((n) => !Number.isNaN(n) && n > 0)
		: [];
	const paged = Math.max(
		1,
		parseInt(url.searchParams.get('paged') || url.searchParams.get('page') || '1', 10) || 1,
	);
	return { cat, proj, paged };
}

function buildUrlForNewsListState(qk, { cat, proj, paged }) {
	const u = new URL(window.location.href);
	u.searchParams.delete(`${qk}_cat`);
	u.searchParams.delete(`${qk}_proj`);
	u.searchParams.delete('paged');
	u.searchParams.delete('page');
	if (cat) {
		u.searchParams.set(`${qk}_cat`, cat);
	}
	if (proj && proj.length) {
		u.searchParams.set(`${qk}_proj`, proj.join(','));
	}
	if (paged > 1) {
		u.searchParams.set('paged', String(paged));
	}
	u.hash = '';
	return u.toString();
}

async function fetchNewsListPartial(root, state, signal) {
	const rest = root.dataset.newsListRest;
	if (!rest) {
		return null;
	}
	const qk = root.dataset.newsQk;
	const perPage = root.dataset.newsListPerPage || '9';
	const pageUrl = window.location.href.split('#')[0];
	const params = new URLSearchParams({
		qk,
		per_page: String(perPage),
		page_url: pageUrl,
		paged: String(state.paged || 1),
		block_id: root.dataset.blockId || '',
	});
	if (state.cat) {
		params.set('cat', state.cat);
	}
	if (state.proj && state.proj.length) {
		params.set('proj', state.proj.join(','));
	}
	const sep = rest.includes('?') ? '&' : '?';
	const res = await fetch(`${rest}${sep}${params.toString()}`, {
		credentials: 'same-origin',
		headers: { Accept: 'application/json' },
		signal,
	});
	if (!res.ok) {
		throw new Error(`news-list: ${res.status}`);
	}
	return res.json();
}

const newsListFetchAbort = new WeakMap();

function scrollNewsListToTop(root) {
	if (window.lenis) {
		window.lenis.scrollTo(root, { duration: 1.2, offset: -40 });
		return;
	}
	const top = root.getBoundingClientRect().top + window.scrollY - 40;
	window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
}

async function loadNewsList(root, state, { skipPush, scrollToTop } = {}) {
	const prev = newsListFetchAbort.get(root);
	if (prev) {
		prev.abort();
	}
	const ac = new AbortController();
	newsListFetchAbort.set(root, ac);

	const ajaxMain = root.querySelector('.block-news-list__ajax-main');
	if (!ajaxMain) {
		newsListFetchAbort.delete(root);
		return;
	}
	try {
		const data = await fetchNewsListPartial(root, state, ac.signal);
		if (ac.signal.aborted || !data || typeof data.html !== 'string') {
			return;
		}
		ajaxMain.innerHTML = data.html;
		if (!skipPush) {
			const qk = root.dataset.newsQk;
			const next = buildUrlForNewsListState(qk, state);
			window.history.pushState({ brixxNewsList: true, qk }, '', next);
		}
		document.dispatchEvent(new CustomEvent('news-list:updated', { detail: { root } }));
		if (scrollToTop) {
			scrollNewsListToTop(root);
		}
	} catch (e) {
		if (e.name === 'AbortError') {
			return;
		}
		// eslint-disable-next-line no-console
		console.error(e);
	} finally {
		if (newsListFetchAbort.get(root) === ac) {
			newsListFetchAbort.delete(root);
		}
	}
}

function onNewsListClick(e) {
	const root = e.target.closest('.block-news-list[data-news-list-ajax="1"]');
	if (!root || !root.contains(e.target)) {
		return;
	}
	const ajaxMain = root.querySelector('.block-news-list__ajax-main');
	if (!ajaxMain || !ajaxMain.contains(e.target)) {
		return;
	}
	const qk = root.dataset.newsQk;

	const filterLink = e.target.closest('a.news-list-filter-btn');
	if (filterLink) {
		e.preventDefault();
		const cat = filterLink.dataset.newsCat || '';
		const { proj } = parseNewsListStateFromUrl(window.location.href, qk);
		void loadNewsList(root, { cat, proj, paged: 1 });
		return;
	}

	const pageLink = e.target.closest('.block-news-list__pagination a');
	if (pageLink) {
		e.preventDefault();
		const href = pageLink.getAttribute('href');
		if (!href) {
			return;
		}
		const u = new URL(href, window.location.origin);
		const state = parseNewsListStateFromUrl(u, qk);
		void loadNewsList(root, state, { scrollToTop: true });
	}
}

function syncProjectFilterFromForm(form) {
	const blockRoot = form.closest('.block-news-list');
	if (!blockRoot) {
		return;
	}
	const qk = blockRoot.dataset.newsQk;
	if (!qk) {
		return;
	}
	const checked = Array.from(form.querySelectorAll(`input[name="${qk}_proj[]"]:checked`)).map((i) =>
		parseInt(i.value, 10),
	);
	const hiddenCat = form.querySelector(`input[name="${qk}_cat"]`);
	const cat =
		hiddenCat && hiddenCat.value
			? hiddenCat.value
			: parseNewsListStateFromUrl(window.location.href, qk).cat;

	const ajaxRoot = form.closest('.block-news-list[data-news-list-ajax="1"]');
	if (ajaxRoot) {
		void loadNewsList(ajaxRoot, { cat, proj: checked, paged: 1 });
		return;
	}
	form.submit();
}

function onNewsListSubmit(e) {
	const form = e.target.closest('.block-news-list__project-form');
	if (!form) {
		return;
	}
	const ajaxRoot = form.closest('.block-news-list[data-news-list-ajax="1"]');
	if (!ajaxRoot) {
		return;
	}
	e.preventDefault();
	syncProjectFilterFromForm(form);
}

function onNewsListProjectFilterChange(e) {
	const input = e.target;
	if (!(input instanceof HTMLInputElement) || input.type !== 'checkbox') {
		return;
	}
	const name = input.getAttribute('name') || '';
	if (!name.endsWith('_proj[]')) {
		return;
	}
	const form = input.closest('.block-news-list__project-form');
	if (!form) {
		return;
	}
	syncProjectFilterFromForm(form);
}

function onPopState() {
	document.querySelectorAll('.block-news-list[data-news-list-ajax="1"]').forEach((root) => {
		const qk = root.dataset.newsQk;
		const state = parseNewsListStateFromUrl(window.location.href, qk);
		void loadNewsList(root, state, { skipPush: true });
	});
}

let newsListListenersBound = false;

function initNewsLists() {
	if (newsListListenersBound) {
		return;
	}
	newsListListenersBound = true;
	document.addEventListener('click', onNewsListClick);
	document.addEventListener('submit', onNewsListSubmit);
	document.addEventListener('change', onNewsListProjectFilterChange);
	window.addEventListener('popstate', onPopState);
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initNewsLists, { once: true });
} else {
	initNewsLists();
}

export { loadNewsList, parseNewsListStateFromUrl };
